import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { getMerchantMe, getShippingZones, updateMerchantMe } from '../lib/api';

export function ProfilePage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  const { data: zonesData } = useQuery({ queryKey: ['shipping-zones'], queryFn: getShippingZones });
  const zones = zonesData?.zones ?? [];
  const [form, setForm] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  // Admin-controlled editability. Missing config = editable (unchanged behavior).
  const editable = data?.profileEditable;
  const can = (k: keyof NonNullable<typeof editable>) => editable?.[k] ?? true;
  const anyEditable =
    can('businessName') ||
    can('ownerName') ||
    can('contactNumber') ||
    can('contactEmail') ||
    can('pickupAddress');

  useEffect(() => {
    if (data) {
      const pa = data.merchant.pickupAddress as { line1?: string; city?: string };
      setForm({
        businessName: data.merchant.businessName,
        ownerName: data.merchant.ownerName ?? '',
        contactNumber: data.merchant.contactNumber ?? '',
        contactEmail: data.merchant.contactEmail ?? '',
        pickupRegionCode: data.merchant.pickupRegionCode ?? '',
        pickupLine1: pa?.line1 ?? '',
        pickupCity: pa?.city ?? '',
      });
    }
  }, [data]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  const mut = useMutation({
    meta: { successMessage: 'Profile saved.' },
    mutationFn: () => {
      // Only submit fields the merchant is allowed to change. Pickup address +
      // region are gated as one group.
      const body: Record<string, unknown> = {};
      if (can('businessName')) body.businessName = form.businessName;
      if (can('ownerName')) body.ownerName = form.ownerName;
      if (can('contactNumber')) body.contactNumber = form.contactNumber || null;
      if (can('contactEmail')) body.contactEmail = form.contactEmail || null;
      if (can('pickupAddress')) {
        body.pickupRegionCode = form.pickupRegionCode || null;
        body.pickupAddress = {
          line1: form.pickupLine1 || undefined,
          city: form.pickupCity || undefined,
          regionCode: form.pickupRegionCode || undefined,
        };
      }
      return updateMerchantMe(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-me'] });
      setSaved(true);
    },
  });

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-2xl font-semibold">Profile</h1>
      <Card>
        <CardHeader>
          <CardTitle>Business & pickup location</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field
            label="Business name"
            value={form.businessName}
            disabled={!can('businessName')}
            onChange={set('businessName')}
          />
          <Field
            label="Owner name"
            value={form.ownerName}
            disabled={!can('ownerName')}
            onChange={set('ownerName')}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Contact number"
              value={form.contactNumber}
              disabled={!can('contactNumber')}
              onChange={set('contactNumber')}
            />
            <Field
              label="Contact email"
              value={form.contactEmail}
              disabled={!can('contactEmail')}
              onChange={set('contactEmail')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Pickup address line"
              value={form.pickupLine1}
              disabled={!can('pickupAddress')}
              onChange={set('pickupLine1')}
            />
            <Field
              label="Pickup city"
              value={form.pickupCity}
              disabled={!can('pickupAddress')}
              onChange={set('pickupCity')}
            />
            <div className="space-y-1">
              <label className="text-sm font-medium">Shipping region</label>
              <Select
                value={form.pickupRegionCode ?? ''}
                disabled={!can('pickupAddress')}
                onValueChange={(v) => {
                  setForm((f) => ({ ...f, pickupRegionCode: v }));
                  setSaved(false);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your area…" />
                </SelectTrigger>
                <SelectContent>
                  {zones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Your pickup region is your shipping origin. It determines courier fees on your products.
          </p>

          {/* Read-only account login email. */}
          <div className="rounded-md border bg-muted/30 p-3 text-xs">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-muted-foreground">Login email</span>
              <span className="font-mono">{data?.merchant.ownerEmail ?? '—'}</span>
            </div>
            <p className="mt-1 text-muted-foreground">
              Your login email can't be changed here — contact admin.
            </p>
          </div>

          {anyEditable && (
            <div className="flex items-center gap-3">
              <Button disabled={mut.isPending} onClick={() => mut.mutate()}>
                {mut.isPending ? 'Saving…' : 'Save'}
              </Button>
              {saved && <span className="text-sm text-success">Saved</span>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field(props: {
  label: string;
  value?: string;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{props.label}</label>
      <Input value={props.value ?? ''} disabled={props.disabled} onChange={props.onChange} />
    </div>
  );
}
